<template>
  <div v-if="order && order.job.sfid">
    <h2 class="mt-3">
      {{ brandDisplayName }}
    </h2>
    <div class="d-flex align-items-center flex-wrap mt-2 mb-4">
      <JobTypes :job="order" class="mr-2" style="margin-top: 3px" />
      <h5 class="mb-0 mr-3">
        <template v-if="jobTypeLabel(order)">
          {{ jobTypeLabel(order) }} {{ $t('at') }}
        </template>
        {{ order.customer && order.customer.name }}
        <template v-if="jobCustomerCity(order)">
          | {{ jobCustomerCity(order) }}
        </template>
        — {{ order.job.name }}
      </h5>
      <b-badge
        v-if="waitingReasonBadge"
        variant="warning"
        class="gid-waiting-badge p-2 ml-2"
      >
        <i class="material-icons align-middle mr-1" style="font-size: 16px">
          schedule
        </i>
        {{ waitingReasonBadge }}
      </b-badge>
    </div>
    <div class="d-flex align-items-center mt-2 mb-4 text-muted">
      <img
        v-if="jobBrandLogo(order)"
        :src="jobBrandLogo(order)"
        :alt="order.brand.name"
        class="brand-logo mr-2"
      />
      <small>{{ order.opportunity && order.opportunity.name }}</small>
    </div>
    <b-alert
      :show="!!successMsg.type"
      dismissible
      :variant="successMsg.type"
      @dismissed="clearSuccessMessage"
    >
      <h3 class="alert-heading">{{ successMsg.title }}</h3>
      <p v-if="successMsg.description">{{ successMsg.description }}</p>
    </b-alert>

    <order-summary
      v-if="availableActionsFetched"
      :order="order"
      :action-options="options"
      :hide-price-purchasing="hidePricePurchasingForOrderSummary"
      :products-order-details="orderDetails"
      @delegate="$refs.delegate.show(order.job.sfid, order.job.name)"
      @sendStateEvent="prepareEventChange"
      @sendProductDelivery="prepareProductDelivery"
      @scrollIntoView="orderIntakeFormsScrollIntoView"
    >
      <template #actions>
        <template v-if="!change">
          <b-row
            ref="order-actions"
            class="border-bottom py-3 py-lg-5 bg-light"
            :class="
              showStartedChecklist ? 'align-items-start' : 'align-items-center'
            "
          >
            <b-col cols="12" lg="3" class="text-muted">
              <h3 class="font-weight-light mb-3 mb-lg-0">
                {{ $t('order-details.actions') }}:
              </h3>
            </b-col>
            <b-col cols="12" lg="9">
              <started-completion-checklist
                v-if="showStartedChecklist"
                :protocol-done="startedProtocolDone"
                :signature-done="startedSignatureDone"
              >
                <template #adjustment-action>
                  <b-button
                    variant="outline-secondary"
                    class="gid-cr-select"
                    @click="crCollapse = !crCollapse"
                  >
                    {{ crCollapse ? $t('cancel') : $t('cancel-reason') }}
                  </b-button>
                </template>
                <template #protocol-action>
                  <b-button
                    @click="prepareProtocolFormAction"
                    variant="outline-warning"
                    class="started-completion__cta"
                  >
                    {{ $t('order-details.action-labels.fill-protocol') }}
                  </b-button>
                </template>
                <template #signature-action>
                  <customer-signature-status
                    :order="order"
                    :variant="CustomerSignatureStatusVariantEnum.REASON"
                    @signatureDeclined="onSignatureDecline"
                    @statusTransitioned="onStatusTransitioned"
                  />
                </template>
              </started-completion-checklist>
              <div
                v-else
                class="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center"
              >
                <!-- Auftragsänderung pinned to the top of the action box (product
                     decision): it must come before the status actions, not after. -->
                <b-button
                  size="lg"
                  variant="outline-secondary"
                  @click="crCollapse = !crCollapse"
                  class="gid-cr-select mr-sm-2 mb-2 mb-sm-0"
                >
                  {{ crCollapse ? $t('cancel') : $t('cancel-reason') }}
                </b-button>
                <span
                  class="mr-sm-2 mb-2 mb-sm-0 text-center"
                  v-if="
                    statusActions.length > 0 || showProtocolFormWithoutChange
                  "
                >
                  {{ $t('or') }}
                </span>
                <b-button
                  v-for="(action, index) in statusActions"
                  :key="index"
                  @click="prepareChange({ status: action.status })"
                  :variant="actionButtonVariantStyle(action)"
                  size="lg"
                  class="text-uppercase px-5 mr-sm-2 mb-2 mb-sm-0 d-block d-sm-inline-block"
                >
                  {{ action.label }}
                </b-button>
                <!-- Protocol form button when job is ON_HOLD waiting for protocol -->
                <b-button
                  v-if="showProtocolFormWithoutChange"
                  @click="prepareProtocolFormAction"
                  variant="primary"
                  size="lg"
                  class="text-uppercase px-5 mr-sm-2 mb-2 mb-sm-0 d-block d-sm-inline-block"
                >
                  {{ $t('order-details.action-labels.fill-protocol') }}
                </b-button>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <cr-collapse
              :extended="crCollapse"
              :cancellations="options.cancellations"
              @crClick="(value) => prepareChange({ cancellation: value })"
            ></cr-collapse>
          </b-row>
          <!-- Customer signature form when signature is still needed (STARTED or after protocol submission) -->
          <b-row
            v-if="showCustomerSignatureWithoutChange && !showStartedChecklist"
            class="bg-light border-bottom py-3"
          >
            <b-col cols="12" lg="9" offset-lg="3">
              <input-holder input-name="customer-signature">
                <customer-signature-status
                  :order="order"
                  :variant="customerSignatureStatusVariant"
                  @signatureDeclined="onSignatureDecline"
                  @statusTransitioned="onStatusTransitioned"
                />
              </input-holder>
            </b-col>
          </b-row>
        </template>
        <template v-else-if="change.loading && change.loading.form">
          <b-row ref="order-actions" class="bg-light border-bottom py-3">
            <b-col cols="12" lg="9" offset-lg="3">
              <b-spinner variant="secondary"></b-spinner>
            </b-col>
          </b-row>
        </template>
        <template v-else>
          <b-row ref="order-actions" class="bg-light border-bottom py-3">
            <b-col
              cols="12"
              lg="9"
              offset-lg="3"
              class="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center"
            >
              <h2 class="font-weight-light mr-sm-2">{{ change.title }}</h2>
              <template
                v-if="
                  change.status &&
                  !change.showCancel &&
                  options.cancellations.length > 0
                "
              >
                <span class="mr-sm-2 mb-2 mb-sm-0 text-center">
                  {{ $t('or') }}
                </span>
                <b-button
                  variant="outline-secondary"
                  @click="crCollapse = !crCollapse"
                  class="gid-cr-select"
                >
                  {{ crCollapse ? $t('cancel') : $t('cancel-reason') }}
                </b-button>
              </template>
            </b-col>
          </b-row>
          <b-row>
            <cr-collapse
              :extended="crCollapse"
              :cancellations="options.cancellations"
              @crClick="(value) => prepareChange({ cancellation: value })"
            ></cr-collapse>
          </b-row>
          <b-row
            class="bg-light border-bottom py-3"
            v-if="
              change.cancellation &&
              change.cancellation.instructions &&
              change.cancellation.instructions[locale]
            "
          >
            <b-col cols="12" lg="9" offset-lg="3">
              <h5 class="font-weight-light mr-sm-2">
                {{ change.cancellation.instructions[locale] }}
              </h5>
            </b-col>
          </b-row>

          <div :class="{ 'gid-sign-view': signView }">
            <ValidationObserver ref="observer">
              <div v-if="signView">
                <h2 class="mt-2">
                  {{
                    $t(
                      'order-details.sign-view.title.' +
                        (change.cancellation ? 'change' : 'protocol'),
                      { job_name: order.job.name },
                    )
                  }}
                </h2>
                <p v-if="change.cancellation">
                  {{ $t('order-details.sign-view.intro') }}
                </p>
                <input-holder
                  v-if="
                    productsConfirmList.length &&
                    invoiceTarget === InvoicingTargetEnum.CUSTOMER
                  "
                  input-name="products"
                >
                  <products-preview :products="productsConfirmList" />
                </input-holder>
                <input-holder
                  v-if="servicesConfirmList.length"
                  :input-name="servicesInput ? 'services_preview' : 'services'"
                >
                  <services-preview
                    :services="servicesConfirmList"
                    :custom_services="customServices"
                    :opportunity-id="order.opportunity.sfid"
                    :services-endpoint="servicesEndpoint"
                    :current-price-list="order.job.price_lists"
                    :use-price-list="true"
                    :show-price-list="!!servicesInput"
                    :show-value-entry="true"
                    :hide-tools="true"
                    :hide-materials="true"
                    :add-vat="currencyVatConfig"
                    :invoiceTarget="invoiceTarget"
                    :additionalProductsListPrice="productsTotalListPrice"
                  />
                </input-holder>
                <input-holder
                  v-if="
                    productsConfirmList.length &&
                    invoiceTarget !== InvoicingTargetEnum.CUSTOMER
                  "
                  input-name="products"
                >
                  <div
                    v-for="(product, index) in productsConfirmList"
                    :key="index"
                  >
                    {{ product.quantity }} x {{ product.name }}
                  </div>
                </input-holder>
                <b-row
                  class="border-bottom py-3"
                  v-if="!showCRFormDocumentation"
                >
                  <b-col cols="12" lg="3" class="text-muted">
                    {{ $t('remark') }}:
                  </b-col>
                  <b-col cols="12" lg="9">
                    <comments :job-api-id="order.api_id" />
                  </b-col>
                </b-row>
              </div>
              <div>
                <template v-for="{ input, available, extra } in displayInputs">
                  <input-holder
                    v-if="available"
                    :key="input.name"
                    :input-name="input.name"
                    :title="extra.title"
                    :type="input.component"
                  >
                    <template v-if="input.component === 'order-intake-forms'">
                      <component
                        :is="input.component"
                        :type="input.type"
                        :input-name="input.name"
                        v-bind.sync="extra"
                        @redirect="orderIntakeFormsRedirect"
                        @fetched="orderIntakeFormsFetched"
                        @summary="orderIntakeFormsSummary"
                        @scrollIntoView="orderIntakeFormsScrollIntoView"
                      />
                    </template>
                    <template v-else>
                      <component
                        :is="input.component"
                        :type="input.type"
                        :input-name="input.name"
                        v-bind.sync="extra"
                        context="partner"
                      />
                    </template>
                  </input-holder>
                </template>
              </div>
              <b-row
                class="bg-light border-bottom py-3"
                v-if="change.description"
              >
                <b-col cols="12" lg="9" offset-lg="3">
                  {{ change.description }}
                </b-col>
              </b-row>
              <b-row class="bg-light border-bottom py-3" v-if="hasAction">
                <b-col cols="12" lg="9" offset-lg="3">
                  <b-alert
                    v-if="showProtocolSubmittedMessage"
                    show
                    :variant="
                      isCustomerSignatureRequired && isCustomerSignatureMissing
                        ? 'warning'
                        : 'success'
                    "
                    class="mb-3"
                  >
                    <i class="fa fa-check-circle mr-2"></i>
                    {{
                      isCustomerSignatureRequired && isCustomerSignatureMissing
                        ? $t(
                            'order-details.completion.protocol-saved-signature-pending',
                          )
                        : $t('order-details.protocol-submitted')
                    }}
                  </b-alert>
                  <b-button
                    v-if="showSignViewButton"
                    size="lg"
                    variant="primary"
                    @click="showSignView"
                    class="mb-2 mb-sm-0 mr-sm-2 d-block d-sm-inline-block"
                  >
                    {{ $t('order-details.sign-view.button') }}
                  </b-button>

                  <b-button
                    v-if="
                      showConfirmButton &&
                      !crCollapse &&
                      !showCRFormDocumentation
                    "
                    @click="confirmChange"
                    :disabled="change.loading && change.loading.button"
                    variant="primary"
                    size="lg"
                    class="text-uppercase px-5 mr-sm-2 mb-2 mb-sm-0 d-block d-sm-inline-block"
                    :class="{
                      'btn-confirm-action-warning':
                        isCustomerSignatureMissing &&
                        !!customerSignatureStatusVariant,
                    }"
                  >
                    <b-spinner
                      v-if="change.loading && change.loading.button"
                      class="gid-spinner--button mr-2 ml-n2"
                    ></b-spinner>
                    {{ change.confirmLabel }}
                  </b-button>

                  <b-button
                    v-if="crCollapse"
                    @click="crCollapse = false"
                    variant="outline-secondary"
                    size="lg"
                    class="text-uppercase px-5 mr-sm-2 mb-2 mb-sm-0 d-block d-sm-inline-block"
                  >
                    {{ $t('cancel') }}
                  </b-button>

                  <b-button
                    v-if="change.showCancel || signView"
                    variant="outline-secondary"
                    @click="cancelChange"
                    size="lg"
                    class="mr-sm-2 mb-2 mb-sm-0 d-block d-sm-inline-block"
                  >
                    {{ $t('cancel') }}
                  </b-button>
                  <div
                    class="text-muted my-3"
                    v-if="
                      useSignView &&
                      !uploadImages &&
                      !signView &&
                      showUploadCheckbox
                    "
                  >
                    {{ $t('or') }}
                  </div>
                  <b-form-checkbox
                    :class="['mt-3', { 'text-muted': uploadImages }]"
                    v-model="uploadImages"
                    v-if="showUploadCheckbox"
                  >
                    <h5>{{ $t('upload-signed-protocol') }}</h5>
                  </b-form-checkbox>
                </b-col>
              </b-row>
              <input-holder
                input-name="customer-signature"
                v-if="
                  !!customerSignatureStatusVariant && !showCRFormDocumentation
                "
              >
                <customer-signature-status
                  :order="order"
                  :variant="customerSignatureStatusVariant"
                  @signatureDeclined="onSignatureDecline"
                  @statusTransitioned="onStatusTransitioned"
                />
              </input-holder>
            </ValidationObserver>
          </div>
        </template>

        <b-row v-if="changeResult" class="border-bottom py-3">
          <b-col cols="12" lg="9" offset-lg="3">
            <b-alert show :variant="changeResult.type" class="mb-2">
              <h3 class="alert-heading">{{ changeResult.title }}</h3>
              <p v-if="changeResult.description">
                {{ changeResult.description }}
              </p>
              <template v-if="changeResult.type == 'error'">
                <p>
                  <em>{{ $t('_errors.report-explain') }}</em>
                </p>
                <b-card
                  v-if="
                    changeResult.validationErrors &&
                    changeResult.validationErrors.errors
                  "
                  class="text-body"
                >
                  <div
                    v-for="(input, inputName) in changeResult.validationErrors
                      .errors"
                    :key="inputName"
                  >
                    <h4>{{ $t(`inputs.headings.${inputName}`) }}</h4>
                    <ul class="mb-0">
                      <li v-for="(error, index) in input" :key="index">
                        {{ error }}
                      </li>
                    </ul>
                  </div>
                </b-card>
                <div v-if="changeResult.details">
                  <b-collapse id="result-details" class="my-2" visible>
                    <b-card>
                      <small>
                        <pre>{{ changeResult.details }}</pre>
                      </small>
                    </b-card>
                  </b-collapse>
                  <b-btn
                    variant="outline-secondary"
                    v-b-toggle.result-details
                    class="mt-2"
                  >
                    {{ $t('_errors.buttons.error-details') }}
                  </b-btn>
                </div>
              </template>
            </b-alert>
          </b-col>
        </b-row>
      </template>
    </order-summary>
    <b-row v-else id="summary" class="bg-light border-bottom py-3">
      <b-col cols="12" lg="9" offset-lg="3">
        <b-spinner variant="secondary"></b-spinner>
      </b-col>
    </b-row>
    <delegate-order ref="delegate" @success="loadData" />
  </div>
  <h2 v-else-if="order && !order.job.sfid" class="mt-3 mb-5">
    {{ $t('order-processing') }}
  </h2>
  <not-found v-else-if="order === false" />
  <b-spinner v-else variant="secondary" class="my-3"></b-spinner>
</template>

<script>
/* eslint-disable vue/no-unused-components */
// eslint can't detect the dynamic usage of the components

import axios from 'axios';
import moment from 'moment';
import { mapGetters, mapState } from 'vuex';
import { computed } from 'vue';

import { JobTypeEnum } from '@gid/models/dist/entities/job-type-enum';

import NotFound from '@/views/NotFound.vue';
import { COUNTS_REFRESH } from '@/store/sidebar.module';
import Summary from './Summary.vue';
import InputHolder from './InputHolder.vue';
import JobTypes from '@gid/vue-common/components/JobTypes.vue';
import ServicesPreview from '@gid/vue-common/components/ServicesPreview.vue';
import ProductsPreview from '@gid/vue-common/components/ProductsPreview.vue';
import Comments from '@gid/vue-common/components/Comments';
import AccountDisplay from '@/components/AccountDisplay.vue';
import Appointment from '@gid/vue-common/components/inputs/Appointment.vue';
import DeliveryDate from '@gid/vue-common/components/inputs/DeliveryDate.vue';
import ReadinessDate from '@gid/vue-common/components/inputs/ReadinessDate.vue';
import FilesSelector from '@gid/vue-common/components/inputs/FilesSelector.vue';
import Documentation from './inputs/Documentation.vue';
import OrderIntakeForms from '@gid/vue-common/components/OrderIntakeForms';
import CrCollapse from '@/components/CrCollapse';
import CustomerSignatureStatus, {
  CustomerSignatureStatusVariantEnum,
} from '@/components/CustomerSignatureStatus';
import StartedCompletionChecklist from '@/components/StartedCompletionChecklist.vue';
import Products from '@gid/vue-common/components/inputs/Products.vue';
import Services from '@gid/vue-common/components/inputs/Services.vue';
import CommentsInput from '@gid/vue-common/components/inputs/Comments.vue';
import checkJobStatus from '@/mixins/jobStatusCheckMixin';
import orderResumeCheck from '@/mixins/orderResumeCheckMixin';
import jobDisplay from '@/mixins/jobDisplayMixin';
import DelegateOrder from './DelegateOrder';
import { BIcon, BIconExclamationTriangleFill } from 'bootstrap-vue';

import {
  CONTEXT_CREATE,
  CONTEXT_DESTROY,
  STORE_API_ERRORS,
} from '@gid/vue-common/store/validation.module/types';
import { filterNonInputErrors } from '@gid/vue-common/store/validation.module/utils';
import {
  JOB_INIT_INPUTS,
  JOB_UPDATE_EXTRA_INPUT,
  JOB_SET_EDIT,
} from '@gid/vue-common/store/jobs.module';

import {
  READINESS_DATE,
  DELIVERY_DATE,
  APPOINTMENT_DATE,
  PLANNING_CUSTOMER,
  PLANNING_PARTNER,
  DOCUMENTATION,
  DOCUMENTATION_PICTURE,
  CANCELLATION_DOCUMENTATION,
  PRODUCTS,
  PROFESSIONAL_COMMENT,
  SERVICES,
} from '@gid/vue-common/components/inputs/_types';
import { CondOperator, RequestQueryBuilder } from '@dataui/crud-request';
import { CommentActionTypeEnum } from '@gid/models/dist/entities/comment';
import {
  CARRIER_UNKNOWN_ID,
  CancellationReasonCategoryEnum,
  CustomerSignatureReasonEnum,
  CustomerSignatureStatusEnum,
  FormOrderModificationCategoryEnum,
  GeneralInvoicingTargetEnum,
  InvoicingTargetEnum,
  JobStatusEnum,
  PARTNER_TRACKING_ID,
  toGenericInvoicingTarget,
  UserRoleEnum,
} from '@gid/models';

import ErrorMixin from '@gid/vue-common/components/inputs/mixins/ErrorMixin';

const VALIDATION_CONTEXT_PREFIX = 'order';
const PARTNER_HIDDEN_CUSTOM_SERVICE_TYPES = [
  '900.Prepayment',
  '901.DeductionOfPrepayment',
  '130.Kommission',
];
const inputsOrder = [
  PRODUCTS,
  SERVICES,
  PROFESSIONAL_COMMENT,
  READINESS_DATE,
  DELIVERY_DATE,
  APPOINTMENT_DATE,
  PLANNING_CUSTOMER,
  PLANNING_PARTNER,
  DOCUMENTATION_PICTURE,
  DOCUMENTATION,
  CANCELLATION_DOCUMENTATION,
];
export const BAYWA_DIENSTWAGENFAHRER_OPP = '0060800000vv2K6AAI';
const VODAFONE_OPPORTUNITY_SENDERNEUSORTIERUNG = '0060800000tKs8EAAS';

export default {
  components: {
    BIcon,
    // eslint-disable-next-line vue/no-unused-components
    BIconExclamationTriangleFill,
    AccountDisplay,
    Comments,
    NotFound,
    InputHolder,
    JobTypes,
    ServicesPreview,
    ProductsPreview,
    OrderIntakeForms,
    CrCollapse,
    CustomerSignatureStatus,
    StartedCompletionChecklist,
    DelegateOrder,
    'order-summary': Summary,
    'input-appointment_date': Appointment,
    'input-delivery_date': DeliveryDate,
    'input-readiness_date': ReadinessDate,
    'input-files_selector': FilesSelector,
    'input-documentation': Documentation,
    'input-documentation_picture': FilesSelector,
    'input-products': Products,
    'input-services': Services,
    'input-professional_comment': CommentsInput,
  },
  mixins: [checkJobStatus, orderResumeCheck, ErrorMixin, jobDisplay],
  props: {
    job_id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      cr: {},
      order: null,
      availableActionsStatusesLoading: false,
      availableActionsCancellationsLoading: false,
      options: {
        statuses: [],
        cancellations: [],
        jobServiceEvents: [],
      },
      requestMixin: {},
      change: null,
      changeResult: null,
      signView: false,
      uploadImages: false,
      orderDetails: [],
      invoiceTarget: null,
      InvoicingTargetEnum,
      JobStatusEnum,
      CustomerSignatureStatusVariantEnum,
      crCollapse: false,
      scrollActionsIntoView: false,
      new_custom_services: [],
      customServicesErrors: {},
      summaryShown: false,
      selectedReason: {}, // Populated when a user selects a cancellation reason
      currentOpportunityId: 'example-opportunity-id', // Replace with actual data source
      showCRFormDocumentation: false,
      skipAutoStatusAction: false,
      protocolFormAutoTriggered: false,
    };
  },
  computed: {
    ...mapGetters(['locale', 'contactProfiles', 'user', 'access_token']),
    ...mapState({
      successMsg: (state) => {
        return state.validation.successMsg;
      },
    }),
    brandDisplayName() {
      return this.order?.opportunity?.name || '';
    },
    waitingReasonBadge() {
      const states = this.order?.job?.states?.list;
      if (!states || states.length === 0) return null;

      const state = states[0];
      if (
        typeof state !== 'string' ||
        !state.startsWith(this.order.job.status) ||
        !state.includes('.')
      ) {
        return null;
      }

      const translation = this.$t(`state.${state}`);
      if (translation === `state.${state}`) {
        return null;
      }
      return translation;
    },
    isCustomerSignatureRequired() {
      return (
        !this.order.is_remote &&
        !this.order.is_hardware_sale &&
        this.order.is_customer_sign_required
      );
    },
    customerSignatureStatusVariant() {
      // Only show customer signature form from STARTED onwards
      // This ensures the job has a start_timestamp before documentation forms are submitted
      if (
        this.order &&
        this.isCustomerSignatureRequired &&
        [JobStatusEnum.STARTED, JobStatusEnum.FINISHED].includes(
          this.order.job.status,
        )
      ) {
        // Show REASON variant (with both buttons) if protocol was submitted but signature still pending
        // This handles the case right after protocol form submission
        if (
          this.order.job.protocol_submitted &&
          [
            CustomerSignatureStatusEnum.MISSING,
            CustomerSignatureStatusEnum.PENDING,
          ].includes(this.order.job.customer_signature)
        ) {
          return CustomerSignatureStatusVariantEnum.REASON;
        }
        return this.summaryShown
          ? CustomerSignatureStatusVariantEnum.REASON
          : CustomerSignatureStatusVariantEnum.PROMPT;
      }
      // When job is STARTED with PENDING or MISSING customer signature, show REASON variant
      // to allow partner to collect signature or provide decline reason.
      // Signature can be MISSING (not just PENDING) when set back from APPROVAL_PENDING
      // via cancellation reason (e.g., "Unvollständiges Protokoll").
      if (
        this.order &&
        this.isCustomerSignatureRequired &&
        this.order.job.status === JobStatusEnum.STARTED &&
        [
          CustomerSignatureStatusEnum.PENDING,
          CustomerSignatureStatusEnum.MISSING,
        ].includes(this.order.job.customer_signature)
      ) {
        return CustomerSignatureStatusVariantEnum.REASON;
      }
      return null;
    },
    isCustomerSignatureMissing() {
      return [
        CustomerSignatureStatusEnum.MISSING,
        CustomerSignatureStatusEnum.PENDING,
      ].includes(this.order.job.customer_signature);
    },
    showCustomerSignatureWithoutChange() {
      // Show customer signature form when no change action is in progress but signature is needed
      // Job is STARTED with protocol submitted but customer signature still pending
      if (!this.order || !this.isCustomerSignatureRequired) {
        return false;
      }

      const customerSignaturePending = [
        CustomerSignatureStatusEnum.MISSING,
        CustomerSignatureStatusEnum.PENDING,
      ].includes(this.order.job.customer_signature);

      if (
        [JobStatusEnum.STARTED, JobStatusEnum.FINISHED].includes(
          this.order.job.status,
        ) &&
        this.order.job.protocol_submitted &&
        customerSignaturePending
      ) {
        return true;
      }

      return false;
    },
    showProtocolFormWithoutChange() {
      // Show protocol form when job is STARTED and protocol has not been submitted yet.
      // Protocol substatuses (protocol-form.pending) live under STARTED status.
      if (!this.order || this.change) {
        return false;
      }

      return (
        this.order.job.status === JobStatusEnum.STARTED &&
        !this.order.job.protocol_submitted
      );
    },
    usesProtocolForm() {
      // Job types that require an execution-protocol form during `started`.
      // Mirrors the job-type set used by showProtocolSubmittedMessage / invokeProtocolIntake.
      return (
        !!this.order &&
        (this.order.is_precheck ||
          this.order.is_installation ||
          this.order.is_meter_application ||
          this.order.is_installation_preparation ||
          this.order.is_maintenance ||
          this.order.is_defect_elimination)
      );
    },
    startedProtocolDone() {
      return !!this.order?.job?.protocol_submitted;
    },
    startedSignatureDone() {
      // Signature step is settled once it is no longer missing/pending.
      return !!this.order && !this.isCustomerSignatureMissing;
    },
    showStartedChecklist() {
      // Persistent two-step overview for a STARTED job that requires BOTH an
      // execution protocol and a customer signature (the two-protocol confusion
      // case). Stays visible until both steps are done — at which point the job
      // can complete — and is hidden during the full-screen sign view and
      // cancellation flows. Derived from job flags (not states.list, which is
      // null for these jobs).
      return (
        !!this.order &&
        this.order.job.status === JobStatusEnum.STARTED &&
        this.usesProtocolForm &&
        this.isCustomerSignatureRequired &&
        (!this.startedProtocolDone || !this.startedSignatureDone) &&
        !this.signView &&
        !this.isApplyingCancellationReason
      );
    },
    newCustomServicesHaveErrors() {
      return this.new_custom_services.some(
        (srvc) => !srvc.service_type || !srvc.name || srvc.name.trim() === '',
      );
    },
    productsTotalListPrice() {
      return this.order.products
        ?.map((product) => product.price_listing * product.quantity)
        .reduce((a, b) => a + b, 0);
    },
    actionsLabels() {
      let startedActionLabel = this.order.job.is_reclamation
        ? this.$t('order-details.action-labels.reclamation-start')
        : this.$t('order-details.action-labels.start');
      let appointmentConfirmedActionLabel = this.order.job.is_reclamation
        ? this.$t('order-details.action-labels.reclamation-appointment-set-up')
        : this.$t('order-details.action-labels.appointment-set-up');

      if (this.order.is_precheck) {
        startedActionLabel = this.$t(
          'order-details.action-labels.precheck-start',
        );
        appointmentConfirmedActionLabel = this.$t(
          'order-details.action-labels.precheck-appointment-set-up',
        );
      } else if (this.order.is_activation) {
        startedActionLabel = this.$t(
          'order-details.action-labels.activation-start',
        );
        appointmentConfirmedActionLabel = this.$t(
          'order-details.action-labels.activation-appointment-set-up',
        );
      } else if (this.order.is_meter_application) {
        startedActionLabel = this.$t(
          'order-details.action-labels.meter-application',
        );
      } else if (this.order.is_installation_preparation) {
        startedActionLabel = this.$t(
          'order-details.action-labels.installation-preparation',
        );
      } else if (this.order.is_defect_elimination) {
        startedActionLabel = this.$t(
          'order-details.action-labels.defect-elimination',
        );
      }

      return {
        [JobStatusEnum.APPOINTMENT_CONFIRMED]: appointmentConfirmedActionLabel,
        [JobStatusEnum.STARTED]: startedActionLabel,
        [JobStatusEnum.FINISHED]: this.$t('finish'),
        [JobStatusEnum.APPROVAL_PENDING]: this.$t(
          'order-details.action-labels.approval-pending',
        ),
      };
    },
    currencyVatConfig() {
      return this.order?.opportunity?.currency_tax_config;
    },
    isAddAppointmentWithMasterAccount() {
      return (
        (this.user?.is_partner_admin_and_master_account ||
          this.user?.is_company_owner_and_master_account) &&
        this.change.status === JobStatusEnum.APPOINTMENT_CONFIRMED
      );
    },
    disallowedActions() {
      return {
        [JobStatusEnum.ACCEPTED]: {
          [JobStatusEnum.STARTED]: {
            conditions: [!this.order.async_appointment],
          },
          [JobStatusEnum.APPOINTMENT_CONFIRMED]: {
            conditions: [
              this.order.async_appointment &&
                Object.values(this.options.statuses)
                  .map((x) => x.name)
                  .some((x) => [JobStatusEnum.STARTED].includes(x)),
            ],
          },
        },
        [JobStatusEnum.APPOINTMENT_CONFIRMED]: {
          [JobStatusEnum.APPOINTMENT_CONFIRMED]: {
            conditions: [true],
          },
        },
        [JobStatusEnum.STARTED]: {
          [JobStatusEnum.STARTED]: {
            conditions: [true],
          },
          [JobStatusEnum.FINISHED]: {
            conditions: [
              this.order?.job?.states?.list?.some(
                (s) =>
                  (s.startsWith('started.customer-signature.') ||
                    s.startsWith('started.protocol-form.')) &&
                  !s.endsWith('.none') &&
                  !s.endsWith('.resolved'),
              ),
            ],
          },
        },
        [JobStatusEnum.FINISHED]: {
          [JobStatusEnum.FINISHED]: {
            conditions: [true],
          },
        },
      };
    },
    statusActions() {
      let actions = [];
      this.options.statuses
        .filter((x) => this.isStatusAllowed(x))
        .forEach(({ name: status }) => {
          if (Object.keys(this.actionsLabels).includes(status)) {
            actions.push({
              status: status,
              label: this.actionsLabels[status],
            });
          }
        });
      return actions;
    },
    changeInputs() {
      return this.change && this.change.inputs ? this.change.inputs : [];
    },
    displayInputs() {
      let inputs = [];
      this.changeInputs.forEach((input) => {
        const displayInput = {
          input,
          available:
            input.enabled &&
            (!input.async ||
              (input.async &&
                input.fetchedChunks.every((x) => x) &&
                (!this.isApplyingCancellationReason ||
                  (this.isApplyingCancellationReason &&
                    input.name == 'cancellation_documentation')))),
          extra: { ...input.extra },
        };
        if (this.useSignView) {
          const isDocumentation =
            input.name === DOCUMENTATION ||
            input.name == CANCELLATION_DOCUMENTATION;
          if (this.signView) {
            if (isDocumentation && input.extra.variants) {
              displayInput.extra.variants = input.extra.variants.filter(
                (variant) => variant == 'protocol_signature',
              );
              // update changed input for order intake form payload:
              if (input.name == 'cancellation_documentation') {
                displayInput.extra.formPayloadCRInputs = this.sendInputs;
              }
              displayInput.extra.title = this.change.cancellation
                ? 'signoff-change'
                : 'signoff-protocol';
              if (
                displayInput.extra.variants.length > 0 &&
                !this.uploadExclusive
              ) {
                inputs.push(displayInput);
              }
            } else if (
              [PLANNING_PARTNER, DOCUMENTATION_PICTURE].includes(input.name) &&
              input.data &&
              input.data.files &&
              input.data.files.length > 0
            ) {
              this.$set(displayInput.extra, 'readonly', true);
              inputs.push(displayInput);
            }
          } else {
            if (isDocumentation && input.extra.variants) {
              displayInput.extra.variants = input.extra.variants.filter(
                (variant) => variant != 'protocol_signature',
              );
              if (
                displayInput.extra.variants.length > 0 &&
                !this.signViewExclusive &&
                this.showUploadCheckbox == this.uploadImages
              ) {
                inputs.push(displayInput);
              }
            } else {
              inputs.push(displayInput);
            }
          }
        } else {
          inputs.push(displayInput);
        }
      });
      return inputs;
    },
    customServices() {
      // During a scope adjustment the partner can remove existing custom
      // services in the services input. Those edits live on the input's
      // `existent_custom_services` (a copy of `order.custom_services` that the
      // CustomServices editor splices in place), not on `this.order`. Read the
      // edited list so removals persist into the submit payload and the customer
      // sign view; fall back to the order's list when no services input exists.
      const existingSource =
        this.servicesInput?.extra?.existent_custom_services ??
        this.order?.custom_services ??
        [];
      const existingCustomServices = existingSource.filter(
        (service) =>
          !PARTNER_HIDDEN_CUSTOM_SERVICE_TYPES.includes(service.service_type),
      );
      const customServices = (this.new_custom_services || []).map((service) => {
        if (service.name || service.hints_de || service.price_purchasing) {
          const pricePurchasing = Number(service.price_purchasing) || 0;
          const marginList =
            this.order.opportunity.custom_services_margin ?? 10;
          const marginSelling =
            this.order.opportunity.custom_services_margin_selling ?? 10;
          return {
            ...service,
            invoicing_target: toGenericInvoicingTarget(
              this.order.opportunity.invoicing_target,
            ),
            price_lists: pricePurchasing + (marginList / 100) * pricePurchasing,
            price_selling:
              pricePurchasing + (marginSelling / 100) * pricePurchasing,
            price_purchasing: pricePurchasing,
          };
        }
      });
      return [...existingCustomServices, ...customServices];
    },
    sendInputs() {
      let inputs = this.$store.getters.jobInputsAllValues(this.job_id);
      if (PLANNING_PARTNER in inputs && !inputs[PLANNING_PARTNER]) {
        delete inputs[PLANNING_PARTNER];
      }
      if (this.isApplyingCancellationReason) {
        delete inputs['order-intake-protocol'];
        if (
          this.requestMixin.cancellation_reason_category ===
          CancellationReasonCategoryEnum.JOB_SCOPE_CHANGE
        ) {
          inputs['custom_services'] = [];
          if (this.customServices.length) {
            inputs['custom_services'] = this.customServices;
          }
        }
      }

      if (inputs.services && inputs.services.length)
        inputs.services = inputs.services.filter(({ service }) => service);

      if (inputs.products && inputs.products.length)
        inputs.products = inputs.products.filter(({ product }) => product);

      return inputs;
    },
    servicesInput() {
      return this.changeInputs.find(({ name }) => name == SERVICES);
    },
    productsInput() {
      return this.changeInputs.find(({ name }) => name == PRODUCTS);
    },
    useSignView() {
      return this.change && this.change.useSignView ? true : false;
    },
    signViewExclusive() {
      return this.change && this.change.signViewExclusive ? true : false;
    },
    uploadExclusive() {
      return this.change && this.change.uploadExclusive ? true : false;
    },
    showSignViewButton() {
      // Don't show sign view button if protocol has already been submitted
      if (this.order?.job?.protocol_submitted) {
        return false;
      }
      return (
        this.useSignView &&
        !this.signView &&
        !this.uploadExclusive &&
        !this.uploadImages
      );
    },
    showConfirmButton() {
      return (
        !this.useSignView ||
        this.signView ||
        this.uploadImages ||
        this.uploadExclusive
      );
    },
    showUploadCheckbox() {
      // Don't show upload option if protocol has already been submitted
      if (this.order?.job?.protocol_submitted) {
        return false;
      }
      return (
        this.useSignView &&
        !this.signViewExclusive &&
        !this.uploadExclusive &&
        !this.signView
      );
    },
    hasOnlyOrderIntakeProtocolInput() {
      if (this.change) {
        if (Object.keys(this.change).length === 0) {
          return false;
        }
        if (Object.keys(this.change).includes('inputs')) {
          if (
            this.change.inputs &&
            Array.isArray(this.change.inputs) &&
            this.change.inputs.length === 0
          ) {
            return false;
          }
        }
      }
      return (
        this.displayInputs.filter((x) => x.available).length === 1 &&
        this.changeInputs.some((x) => x.name === 'order-intake-protocol')
      );
    },
    hasAction() {
      let action = !this.hasOnlyOrderIntakeProtocolInput;
      if (this.isApplyingCancellationReason) {
        action = true;
      }
      return action;
    },
    showProtocolSubmittedMessage() {
      // Show message when protocol has been submitted and job is in a status where protocol form would normally show
      return (
        this.order?.job?.protocol_submitted &&
        [JobStatusEnum.STARTED, JobStatusEnum.FINISHED].includes(
          this.order?.job?.status,
        ) &&
        (this.order?.is_precheck ||
          this.order?.is_installation ||
          this.order?.is_meter_application ||
          this.order?.is_installation_preparation ||
          this.order?.is_maintenance ||
          this.order?.is_defect_elimination)
      );
    },
    isApplyingCancellationReason() {
      return !!this.requestMixin.cancellation_reason_id;
    },
    shouldAllowProfessionalComments() {
      return (
        this.isApplyingCancellationReason &&
        this.requestMixin.cancellation_reason_category ===
          CancellationReasonCategoryEnum.JOB_SCOPE_CHANGE &&
        this.order?.job?.status === JobStatusEnum.ON_HOLD_BEFORE_ACCEPTED
      );
    },
    servicesEndpoint() {
      return `/api/partner/services/for-job/${this.job_id}`;
    },
    servicesConfirmList() {
      return this.servicesInput
        ? this.$store.getters.jobInputValue(this.job_id, SERVICES)
        : this.order.services;
    },
    productsConfirmList() {
      const products = (
        this.productsInput
          ? this.$store.getters.jobInputValue(this.job_id, PRODUCTS)
          : this.order.products
      ).map((el) => {
        const productDef = this.$store.getters.productDefinition(el.product);
        return { ...el, ...{ name: productDef.name } };
      });
      if (!this.isDKVB2C) {
        return products;
      }
      return products.map((el) => {
        if (el.invoicing_target === GeneralInvoicingTargetEnum.BRAND) {
          el.invoicing_target = GeneralInvoicingTargetEnum.CUSTOMER;
        }
        return el;
      });
    },
    isDKVB2C() {
      return this.order?.opportunity?.email_type === 'dkv_b2c';
    },
    allowDuplicateServices() {
      // For now allow this only for th gardena hedges opportunity
      return this.order && this.order.opportunity.sfid === '0061p00000mv76wAAA';
    },
    validationContext() {
      return `${VALIDATION_CONTEXT_PREFIX}-${this.job_id}`;
    },
    /** true so scope-adjustment form hides product purchase price. */
    hidePricePurchasing() {
      return true;
    },

    /** true so scope-adjustment form hides product listing price. */
    hidePriceListing() {
      return true;
    },

    /** true so scope-adjustment form hides product selling price. */
    hidePriceSelling() {
      return true;
    },

    /** false so partner sees service purchase prices in order summary (Aufgaben). */
    hidePricePurchasingForOrderSummary() {
      return false;
    },
    hidePricePurchasingForServices() {
      return false;
    },
    availableActionsFetched() {
      return (
        !this.availableActionsStatusesLoading &&
        !this.availableActionsCancellationsLoading
      );
    },
  },
  watch: {
    job_id(newValue, oldValue) {
      this.protocolFormAutoTriggered = false;
      this.loadData();
      this.destroyValidationContext(oldValue);
      this.createValidationContext(newValue);
    },
    sendInputs: {
      deep: true,
      handler(newValue, oldValue) {
        if (JSON.stringify(newValue) != JSON.stringify(oldValue)) {
          // clear api validation errors
          this.changeResult = null;
        }
      },
    },
    signView(enabled) {
      if (enabled) {
        this.addBodyClass();
      } else {
        this.removeBodyClass();
      }
    },
    availableActionsFetched(value) {
      if (value && this.scrollActionsIntoView) {
        setTimeout(() => {
          this.$refs['order-actions']?.scrollIntoView();
        }, 500);
      }
      // Auto-show protocol form when job is in started.protocol-form.pending
      if (
        value &&
        !this.change &&
        !this.protocolFormAutoTriggered &&
        this.order?.job?.status === JobStatusEnum.STARTED &&
        this.order?.job?.states?.list?.some(
          (s) =>
            s.startsWith('started.protocol-form.') &&
            !s.endsWith('.none') &&
            !s.endsWith('.resolved'),
        )
      ) {
        this.protocolFormAutoTriggered = true;
        this.$nextTick(() => {
          this.prepareProtocolFormAction();
        });
      }
    },
  },
  created() {
    this.loadData();
  },
  beforeDestroy() {
    this.removeBodyClass();
    this.$store.dispatch('validation/STORE_SUCCESS_MESSAGE', {});
  },
  destroyed() {
    this.destroyValidationContext(this.job_id);
  },
  async beforeRouteLeave(to, from, next) {
    // Gently remind the partner of remaining steps before they leave a still
    // incomplete STARTED job. Don't nag when moving forward in the flow
    // (e.g. heading to the customer-signature route or the completion screen).
    const inFlowRoutes = ['order-customer-signature', 'order-complete'];
    if (!this.showStartedChecklist || inFlowRoutes.includes(to.name)) {
      next();
      return;
    }
    const remaining = [];
    if (!this.startedProtocolDone) {
      remaining.push(this.$t('order-details.completion.step-protocol-title'));
    }
    if (!this.startedSignatureDone) {
      remaining.push(this.$t('order-details.completion.step-signature-title'));
    }
    try {
      const proceed = await this.$bvModal.msgBoxConfirm(
        this.$t('order-details.completion.leave-confirm', {
          steps: remaining.join(', '),
        }),
        {
          title: this.$t('order-details.completion.leave-title'),
          okTitle: this.$t('order-details.completion.leave-ok'),
          cancelTitle: this.$t('order-details.completion.leave-cancel'),
          okVariant: 'secondary',
          cancelVariant: 'primary',
          centered: true,
        },
      );
      next(proceed ? undefined : false);
    } catch (e) {
      next();
    }
  },
  methods: {
    clearSuccessMessage() {
      this.$store.dispatch('validation/STORE_SUCCESS_MESSAGE', {});
    },
    resolveOifLocale(fallbackLocale) {
      const localeFromQuery = this.$route?.query?.locale;
      const localeCandidate =
        typeof localeFromQuery === 'string' && localeFromQuery
          ? localeFromQuery
          : this.locale || fallbackLocale;
      const normalizedLocale = String(localeCandidate || '')
        .toLowerCase()
        .split('-')[0];
      return ['de', 'en', 'fr'].includes(normalizedLocale)
        ? normalizedLocale
        : fallbackLocale;
    },
    onSignatureDecline(order) {
      this.order = order;
    },
    /**
     * Handle async status transition after signature decline.
     * Called when jobs-service has processed the ON_HOLD substatus resolution.
     */
    onStatusTransitioned(order) {
      this.order = order;
      // Redirect to the appropriate view based on new status
      this.afterChange(order.job.status);
    },
    addCustomerReachedComment() {
      axios.post(`/data-api/comment`, {
        actionType: CommentActionTypeEnum.PHONE,
        jobApiId: this.order.job.api_id,
        message: 'Kunde hat sich gemeldet, Termin wurde vereinbart.',
        visibleForRoles: [
          UserRoleEnum.ADMIN,
          UserRoleEnum.BRAND,
          UserRoleEnum.PARTNER,
        ],
      });
    },
    addBodyClass() {
      document.body.classList.add('gid-sign-view--open');
    },
    removeBodyClass() {
      document.body.classList.remove('gid-sign-view--open');
    },
    loadData(skipLoading) {
      if (!skipLoading) {
        this.order = null;
      }
      this.scrollActionsIntoView = this.$route.query.showActions;
      axios
        .get(`/api/partner/orders/${this.job_id}`)
        .then((response) => {
          this.order = response.data;
          this.invoiceTarget = this.order.opportunity.invoicing_target;
          this.$store.commit(
            'title',
            this.$t('order-with-number', { num: this.order.job.name }),
          );
          this.fetchProductOrders();
          this.fetchAvailableActions();
          // GID-2608: snapshot the diffed fields so the resume / 1h-idle check can
          // detect background changes to this specific order.
          this.captureChangeBaseline();
        })
        .catch((error) => {
          if (error.response && error.response.status == 404) {
            this.order = false;
          }
        });
    },
    async fetchProductOrders() {
      const orderedQuery = RequestQueryBuilder.create({
        search: {
          'job.id': { [CondOperator.IN]: [this.job_id] },
        },
      }).query();
      const productOrder = await axios
        .get(`/documents-api/product-orders?${orderedQuery}`)
        .then(({ data }) => data);

      this.orderDetails = [
        ...new Set(
          productOrder
            .sort(
              (firstEl, secondEl) => secondEl.created_at - firstEl.created_at,
            )
            .map((order) => ({
              id: order.id,
              items: order.items.map((item) => ({
                id: parseInt(item.product.id),
                name: item.productName,
              })),
              url: order?.carrier?.urlPattern?.replace(
                '{ID}',
                order.trackingID,
              ),
              trackingID: order.trackingID,
              carrierId: order.carrierId,
              shipTo: order.shipTo,
            })),
        ),
      ];
    },
    fetchAvailableActions() {
      this.availableActionsStatusesLoading = true;
      axios
        .post(`/api/partner/orders/set-status`, {
          preview: true,
          job_id: this.job_id,
        })
        .then((response) => {
          this.options.statuses = response.data.statuses
            .filter((status) => {
              return status.allowed;
            })
            .map((status) => ({
              name: status.job_status,
              label:
                status.partner_name[this.locale] ||
                status.partner_name['en'] ||
                status.job_status,
            }));
          this.tryAutoStatusAction();
        })
        .finally(() => {
          this.availableActionsStatusesLoading = false;
        });

      this.availableActionsCancellationsLoading = true;
      axios
        .get(`/api/partner/orders/${this.job_id}/cancellation-reasons`)
        .then((response) => {
          this.options.cancellations = response.data;
        })
        .catch((error) => {
          this.options.cancellations = error;
        })
        .finally(() => {
          this.availableActionsCancellationsLoading = false;
        });

      axios
        .get(
          `/jobs-api/status-rules/job/${this.order.job.api_id}/events-available`,
        )
        .then(({ data }) => {
          this.options.jobServiceEvents = data
            // handled specially in the orders list
            .filter((event) => event != 'CUSTOMER_UNREACHABLE');
        });
    },
    tryAutoStatusAction() {
      // Skip auto action if flagged (e.g., after protocol submission with pending signature)
      if (this.skipAutoStatusAction) {
        this.skipAutoStatusAction = false;
        return;
      }
      // For a still-incomplete two-step `started` job, don't auto-open the
      // bundled finish flow — the completion checklist is the landing surface
      // and drives the protocol / signature steps from there.
      if (this.showStartedChecklist) {
        return;
      }
      if (this.statusActions.length == 1) {
        this.prepareChange({ status: this.statusActions[0].status });
      }
    },
    prepareChange({ status, cancellation }) {
      let changeMixin;
      if (cancellation) {
        let cr = this.options.cancellations.find((cr) => cr.id == cancellation);
        changeMixin = {
          title: cr.description[this.locale],
          confirmLabel: this.$t('confirm'),
          showCancel: true,
          cancellation: cr,
          signViewExclusive: cr.sign_off,
          uploadExclusive: !cr.sign_off,
        };
        this.requestMixin = {
          cancellation_reason_id: cancellation,
          cancellation_reason_category: cr.reason_category,
        };
        this.cr = cr;
      } else if (status) {
        let confirmLabel = this.actionsLabels[status];
        changeMixin = {
          title: this.actionsLabels[status],
          status: status,
          confirmLabel,
          showCancel: this.statusActions.length > 1,
          description:
            status === JobStatusEnum.APPROVAL_PENDING
              ? this.$t(
                  'order-details.action-labels.approval-pending-description',
                )
              : undefined,
        };
        this.requestMixin = {
          new_status: status,
        };
      }

      this.changeResult = null;
      this.change = { loading: { form: true } };

      axios
        .post(`/api/partner/orders/set-status`, {
          preview: true,
          job_id: this.job_id,
          ...this.requestMixin,
        })
        .then(async (response) => {
          changeMixin.useSignView = this.checkIfSignView(response.data.inputs);
          this.change = {
            inputs: await this.initInputs(response.data.inputs),
            ...changeMixin,
          };
          this.crCollapse = false;
        })
        .catch((error) => {
          // Handle preview request failure (e.g., status transition not allowed)
          // Reset change state and reload to get fresh data
          console.error('Status change preview failed:', error);
          this.change = null;
          this.loadData();
        });
    },
    async prepareProtocolFormAction() {
      // Prepare the protocol form action when job is STARTED with protocol-form.pending
      // This is triggered when the partner wants to fill out the protocol form
      this.changeResult = null;
      this.change = { loading: { form: true } };

      const changeMixin = {
        title: this.$t('order-details.action-labels.fill-protocol'),
        confirmLabel: this.$t('order-details.action-labels.submit-protocol'),
        showCancel: true,
        isProtocolFormAction: true,
      };

      // Initialize inputs with an empty object since we're not doing a status transition
      // The invokeProtocolIntake method will add the protocol form input
      const inputs = await this.initInputs({});

      this.change = {
        inputs,
        ...changeMixin,
      };
    },
    prepareEventChange(event) {
      this.changeResult = null;
      this.change = {
        loading: { button: true },
        title: this.$t(`state-events.${event}`),
        confirmLabel: this.$t(`state-events.${event}`),
        event,
      };
      this.sendJobStateEvent();
    },
    prepareProductDelivery(productOrder) {
      this.changeResult = null;
      this.change = {
        loading: { button: true },
        title: this.$t('order-details.confirm-labels.received-product'),
        confirmLabel: this.$t('order-details.confirm-labels.received-product'),
        productOrder,
      };
      this.sendProductDeliveryEvent();
    },
    checkIfSignView(inputs) {
      return (
        (inputs[DOCUMENTATION] &&
          inputs[DOCUMENTATION].state == 'required' &&
          inputs[DOCUMENTATION].variants.includes('protocol_signature')) ||
        (inputs[CANCELLATION_DOCUMENTATION] &&
          inputs[CANCELLATION_DOCUMENTATION].variants.includes(
            'protocol_signature',
          ))
      );
    },
    orderIntakeFormsScrollIntoView(data) {
      const element = this.$refs['order-actions'];
      if (element) {
        element.scrollIntoView({
          behavior: data.behavior,
          block: data.block,
        });
      }
    },
    orderIntakeFormsSummary(data) {
      if (!this.showCRFormDocumentation) {
        this.summaryShown = data;
      }
    },
    orderIntakeFormsRedirect(data) {
      this.showCRFormDocumentation = false;
      this.cr = {};

      // Merge form submission data with local order data
      // Form data takes priority for fields it provides
      const jobData = {
        protocol_submitted: this.order.job.protocol_submitted,
        customer_signature: this.order.job.customer_signature,
        customer_signature_reason: this.order.job.customer_signature_reason,
        status: this.order.job.status,
        sfid: this.order.job.sfid,
        ...data.job,
      };

      // Check if both protocol is submitted AND customer signature is collected
      // In this case, the jobs-service will transition the job to APPROVAL_PENDING
      // so we should redirect to completed even if the async transition hasn't happened yet
      const signatureCollected =
        jobData.customer_signature === CustomerSignatureStatusEnum.TAKEN ||
        (jobData.customer_signature === CustomerSignatureStatusEnum.DECLINED &&
          jobData.customer_signature_reason !== null &&
          jobData.customer_signature_reason !==
            CustomerSignatureReasonEnum.MISSING_CUSTOMER);

      // Check if customer signature is pending (protocol submitted but signature not yet collected)
      const signaturePending =
        jobData.protocol_submitted &&
        !signatureCollected &&
        [
          CustomerSignatureStatusEnum.MISSING,
          CustomerSignatureStatusEnum.PENDING,
        ].includes(jobData.customer_signature);

      if (
        (jobData.status === JobStatusEnum.APPROVAL_PENDING ||
          (jobData.protocol_submitted && signatureCollected)) &&
        this.order.job.customer_signature_reason !==
          CustomerSignatureReasonEnum.MISSING_CUSTOMER
      ) {
        if (jobData.sfid) {
          this.afterChange(JobStatusEnum.APPROVAL_PENDING);
        }
      } else if (signaturePending) {
        // Protocol was submitted but customer signature is still pending
        // The jobs-service will transition the job to ON_HOLD asynchronously
        // Skip auto status action to avoid race condition with the async transition
        this.$store.dispatch('validation/STORE_SUCCESS_MESSAGE', {
          type: 'success',
          title: this.$t('order-details.completion.protocol-saved-title'),
          description: this.$t('order-details.completion.protocol-saved-desc'),
        });
        this.new_custom_services = [];
        this.signView = false;
        this.change = null;
        this.skipAutoStatusAction = true;
        this.loadData();
      } else {
        this.new_custom_services = [];
        this.signView = false;
        this.loadData();
        this.change = null;
      }
    },
    orderIntakeFormsFetched(data) {
      const inputIndex = this.change.inputs.findIndex(
        (x) => x.name === 'order-intake-protocol',
      );
      if (inputIndex !== -1) {
        this.change.inputs[inputIndex].extra.fetched = data;
        this.$store.dispatch(JOB_UPDATE_EXTRA_INPUT, {
          jobId: this.job_id,
          inputName: this.change.inputs[inputIndex].name,
          extra: this.change.inputs[inputIndex].extra,
        });
      }
    },
    buildProtocolIntakeComponentOutput() {
      let extra = {
        elementId: this.job_id,
        jobId: this.job_id,
        validationContext: this.validationContext,
        validationPath: 'order-intake-forms',
        opportunitySfid: this.order.opportunity.sfid,
        orderApiId: this.order.api_id,
        accessToken: this.access_token,
      };

      const componentOutput = {
        name: 'order-intake-protocol',
        type: 'required',
        fetchedChunks: [false],
        async: true,
        component: 'order-intake-forms',
        data: {},
        extra,
      };

      return componentOutput;
    },
    async assignComponentOutputDataFromProtocolIntakeForm(
      componentOutput,
      formType,
      protocolType,
    ) {
      const intermediaryFormUrl = `/order-intake-forms-api/form/${this.order.opportunity.sfid}/form-type/${formType}/protocol-type/${protocolType}/category/${FormOrderModificationCategoryEnum.DOCUMENTATION}`;
      let intermediaryForm = null;
      try {
        const intermediaryFormResponse = await axios.get(intermediaryFormUrl);
        intermediaryForm = intermediaryFormResponse.data;
        if (intermediaryForm) {
          componentOutput.extra.locale = this.resolveOifLocale(
            intermediaryForm.locale,
          );
          componentOutput.extra.form = intermediaryForm.id;
          componentOutput.extra.theme = intermediaryForm.theme;
          componentOutput.enabled = true;
        }
      } catch (err) {
        console.error(err);
        componentOutput.enabled = false;
      } finally {
        componentOutput.fetchedChunks[0] = true;
      }
      return componentOutput;
    },
    async getCROrDocumentationInput(_input, inputName, formType) {
      let crForm = null;
      try {
        const response = await axios.get(
          `/order-intake-forms-api/form/${this.order.opportunity.sfid}/form-type/${formType}/protocol-type/default/category/${FormOrderModificationCategoryEnum.CONFIRMATION}/cr/${this.cr?.name}`,
        );
        crForm = response.data;
      } catch (error) {
        console.warn('No CR form found or error fetching CR form:', error);
      }

      // If CR form is found => return the CR input
      if (crForm && crForm.id) {
        this.showCRFormDocumentation = true;
        return {
          name: 'documentation-cr-form',
          type: 'required',
          component: 'order-intake-forms',
          async: true,
          fetchedChunks: [true],
          data: {},
          extra: {
            variants: _input.variants,
            locale: this.resolveOifLocale(crForm.locale),
            form: crForm.id,
            theme: crForm.theme,
            // typical props for order-intake-forms
            elementId: this.order.job.sfid,
            opportunitySfid: this.order.opportunity.sfid,
            orderApiId: this.order.api_id,
            accessToken: this.access_token,
            formPayloadCRInputs: this.sendInputs,
          },
        };
      }

      // Otherwise => fallback to normal documentation
      this.showCRFormDocumentation = false;
      return {
        component: 'input-documentation',
        extra: {
          variants: _input.variants,
          placeOfSignature: this.order.customer.shipping_address.city,
          ga: {
            opportunityName: this.order.opportunity.name,
          },
          documentationType: this.order.opportunity.documentation_type,
          inputName: inputName,
          customerName: this.order.customer.name,
          context: 'partner',
        },
      };
    },
    async invokeProtocolIntake(_formType, inputsArray) {
      // Show protocol form if protocol hasn't been submitted yet.
      // Covers STARTED/FINISHED (normal flow), ON_HOLD with protocol-form.pending,
      // and ON_HOLD after admin setback (e.g., "Unvollständiges Protokoll").
      if (
        !this.order.job.protocol_submitted &&
        [
          JobStatusEnum.STARTED,
          JobStatusEnum.ON_HOLD,
          JobStatusEnum.FINISHED,
        ].includes(this.order.job.status)
      ) {
        const formType = _formType;
        let protocolType = 'default';

        if (formType === 'installation') {
          if (
            this.order.opportunity.sfid ===
            VODAFONE_OPPORTUNITY_SENDERNEUSORTIERUNG
          ) {
            protocolType = 'incident';
          }
        }

        let componentOutputDefinition =
          this.buildProtocolIntakeComponentOutput();
        let componentOutput =
          await this.assignComponentOutputDataFromProtocolIntakeForm(
            componentOutputDefinition,
            formType,
            protocolType,
          );
        if (componentOutput.extra.form) {
          inputsArray.push(componentOutput);
        }
      }
    },
    getFormTypeFromJobType() {
      switch (true) {
        case this.order.is_precheck:
          return JobTypeEnum.PRECHECK;
        case this.order.is_installation:
          return JobTypeEnum.INSTALLATION;
        case this.order.is_meter_application:
          return JobTypeEnum.METER_APPLICATION;
        case this.order.is_installation_preparation:
          return JobTypeEnum.INSTALLATION_PREPARATION;
        case this.order.is_maintenance:
          return JobTypeEnum.MAINTENANCE;
        case this.order.is_defect_elimination:
          return JobTypeEnum.DEFECT_ELIMINATION;
        case this.order.is_hardware_sale:
          return JobTypeEnum.HARDWARE_SALE;
        case this.order.is_reclamation:
          return JobTypeEnum.RECLAMATION;
        default:
          return null;
      }
    },
    async initInputs(inputs) {
      let inputsArray = [];
      const allowOptionalInputs = [];
      if (this.shouldAllowProfessionalComments) {
        allowOptionalInputs.push(PROFESSIONAL_COMMENT);
      }
      if (this.requestMixin.new_status == 'finished') {
        allowOptionalInputs.push(PLANNING_PARTNER);
      }
      let minDate = this.order.job.readiness_date
        ? moment(this.order.job.readiness_date)
        : this.order.job.date_expected
        ? moment(this.order.job.date_expected)
        : moment(this.order.job.created_date)
            .startOf('day')
            .add(this.order.opportunity.assembly_time_days || 0, 'days');
      if (minDate.isBefore(moment().startOf('day'))) {
        minDate = moment().startOf('day');
      }
      if (this.order.is_precheck) {
        await this.invokeProtocolIntake('precheck', inputsArray);
      } else if (this.order.is_installation) {
        await this.invokeProtocolIntake('installation', inputsArray);
      } else if (this.order.is_meter_application) {
        await this.invokeProtocolIntake('meter_application', inputsArray);
      } else if (this.order.is_installation_preparation) {
        await this.invokeProtocolIntake(
          'installation_preparation',
          inputsArray,
        );
      } else if (this.order.is_maintenance) {
        await this.invokeProtocolIntake('maintenance', inputsArray);
      } else if (this.order.is_defect_elimination) {
        await this.invokeProtocolIntake('defect_elimination', inputsArray);
      } else {
        await this.invokeProtocolIntake('None', inputsArray);
      }

      for (const inputName in inputs) {
        const inputType = inputs[inputName].state;
        let component = `input-${inputName}`;
        let initData = null;
        let extra = {
          jobId: this.job_id,
          validationContext: this.validationContext,
          validationPath: inputName,
        };
        let componentFetchedChunks = [];
        let componentAsync = false;
        let componentEnabled = true;
        if (
          inputType != 'required' &&
          !allowOptionalInputs.includes(inputName)
        ) {
          continue;
        }

        if ([PLANNING_PARTNER, DOCUMENTATION].includes(inputName)) {
          continue;
        }

        if (inputName == DELIVERY_DATE) {
          initData = this.order.job.date_expected;
        } else if (inputName == READINESS_DATE) {
          initData = this.order.job.readiness_date;
          extra.onlyAfterDate = moment().startOf('day');
        } else if (inputName == APPOINTMENT_DATE) {
          initData = {
            start: this.order.job.appointment_start_timestamp,
            end: this.order.job.appointment_end_timestamp,
          };
          extra.multiday = this.order.opportunity.multiday;
          extra.title = this.order.job.is_reclamation
            ? this.$t('reclamation-appointment_date')
            : this.$t('appointment_date');
          if (this.order.is_precheck) {
            extra.title = this.$t('precheck-appointment_date');
          }
          extra.disclaimer = this.$t('disclaimer-appointment_date');

          let readiness_date;
          if (this.order.job.readiness_date || this.order.job.date_expected) {
            readiness_date =
              new Date(this.order.job.readiness_date) >
              new Date(this.order.job.date_expected)
                ? new Date(this.order.job.readiness_date)
                : new Date(this.order.job.date_expected);
          }
          extra.readiness_date = readiness_date;
        } else if (
          [DOCUMENTATION, CANCELLATION_DOCUMENTATION].includes(inputName)
        ) {
          if (this.cr?.name) {
            const formType = this.getFormTypeFromJobType();
            const modifiedInput = await this.getCROrDocumentationInput(
              inputs[inputName],
              inputName,
              formType,
            );

            component = modifiedInput.component;
            extra = { ...extra, ...modifiedInput.extra };
            componentFetchedChunks = modifiedInput?.fetchedChunks || [false];
            componentAsync = modifiedInput?.async || false;
          } else {
            component = `input-documentation`;
            extra = {
              ...extra,
              variants: inputs[inputName].variants,
              placeOfSignature: this.order.customer.shipping_address.city,
              ga: {
                opportunityName: this.order.opportunity.name,
              },
              documentationType: this.order.opportunity.documentation_type,
              inputName: inputName,
              customerName: this.order.customer.name,
              context: 'partner',
            };
            this.showCRFormDocumentation = false;
          }
        } else if (inputName === DOCUMENTATION_PICTURE) {
          component = 'input-files_selector';
          extra = {
            ...extra,
            context: 'partner',
          };
        } else if ([PLANNING_CUSTOMER, PLANNING_PARTNER].includes(inputName)) {
          component = 'input-files_selector';
          extra = {
            ...extra,
            ga: {
              opportunityName: this.order.opportunity.name,
              imagesType: inputName,
            },
            context: 'partner',
          };
        } else if (inputName == PRODUCTS) {
          initData = this.order.products;
          extra = {
            ...extra,
            hidePricePurchasing: this.hidePricePurchasing,
            hidePriceListing: this.hidePriceListing,
            hidePriceSelling: this.hidePriceSelling,
          };
        } else if (inputName == SERVICES) {
          initData = this.order.services.map((service) => ({
            service: service.sfid,
            quantity: service.quantity,
            price_purchasing: service.price_purchasing,
            price_selling: service.price_selling,
            price_lists: service.price_lists,
            value_entry: service.value_entry || 1,
            api_id: service.api_id,
            product_id: service.product_id,
            brand_project_id: service.brand_project_id,
          }));

          extra = {
            ...extra,
            services_endpoint: this.servicesEndpoint,
            currentPricePurchasing: this.order.job.price_purchasing,
            currentPriceSelling: this.order.job.price_selling,
            hidePricePurchasing: this.hidePricePurchasingForServices,
            hidePriceSelling: true,
            showLineTotals: true,
            allowDuplicateServices: this.allowDuplicateServices,
            productId: null,
            existent_custom_services: (this.order.custom_services || []).filter(
              (service) =>
                !PARTNER_HIDDEN_CUSTOM_SERVICE_TYPES.includes(
                  service.service_type,
                ),
            ),
            custom_services: this.new_custom_services,
            products: this.order.products,
          };
        } else if (
          inputName === PROFESSIONAL_COMMENT &&
          this.shouldAllowProfessionalComments
        ) {
          initData = this.order.job.professional_comment;
        } else {
          continue;
        }

        inputsArray.push({
          enabled: componentEnabled,
          name: inputName,
          type: inputType,
          fetchedChunks: componentFetchedChunks,
          async: componentAsync,
          data: initData,
          extra,
          component,
        });
      }

      if (
        this.order.is_precheck ||
        this.order.is_installation ||
        this.order.is_meter_application ||
        this.order.is_installation_preparation ||
        this.order.is_maintenance ||
        this.order.is_defect_elimination
      ) {
        const orderIntakeProtocol = inputsArray.find(
          (x) => x.name === 'order-intake-protocol',
        );
        if (orderIntakeProtocol) {
          if (
            [JobStatusEnum.STARTED, JobStatusEnum.FINISHED].includes(
              this.order.job.status,
            )
          ) {
            const disableInputs = [
              'planning_partner',
              'documentation',
              'documentation_picture',
              'protocol_signature',
            ];
            disableInputs.forEach((inputName) => {
              const index = inputsArray.findIndex((i) => i.name === inputName);
              if (index !== -1) {
                inputsArray[index].enabled = false;
              }
            });
          }
        }
      }

      inputsArray.sort(
        (a, b) => inputsOrder.indexOf(a.name) - inputsOrder.indexOf(b.name),
      );
      this.$store.dispatch(JOB_SET_EDIT, {
        init: true,
        jobId: this.job_id,
        opportunity: this.order.opportunity,
        brandId: this.order.brand.id,
        context: 'partner',
      });
      this.$store.dispatch(JOB_INIT_INPUTS, {
        jobId: this.job_id,
        inputs: inputsArray,
        constrains: {
          minDate,
        },
      });

      return inputsArray;
    },
    confirmChange() {
      const hasCustomServiceFromError = this.customServiceHasErrors();
      if (!hasCustomServiceFromError) {
        this.$refs.observer.validate().then((valid) => {
          if (valid) {
            this.change = {
              ...this.change,
              loading: { button: true },
            };
            if (this.change.event) {
              this.sendJobStateEvent();
            } else {
              this.sendChangeRequest();
            }
          }
        });
      }
    },
    sendJobStateEvent() {
      const stateEvent = this.change.event;
      axios
        .post(
          `/jobs-api/status-rules/job/${this.order.job.api_id}/apply-event`,
          {
            type: stateEvent,
          },
        )
        .then(({ data }) => {
          this.changeResult = {
            type: 'success',
            title: this.$t('successful'),
          };
          this.change = null;
          this.loadData(true);
          this.scrollActionsIntoView = true;
          this.$store.dispatch(COUNTS_REFRESH);
          if (stateEvent == 'CUSTOMER_REACHED') {
            this.addCustomerReachedComment();
          }
        });
    },
    sendProductDeliveryEvent() {
      const productOrder = this.change.productOrder;
      axios
        .patch(`/documents-api/product-orders/${productOrder.id}`, {
          trackingID: PARTNER_TRACKING_ID,
          carrier: { id: CARRIER_UNKNOWN_ID },
          comment: {
            message: `
Der Service Partner hat den Empfang der Produkte<br/>
${productOrder.items
  .map((product) => `<strong>${product.name}</strong><br/>`)
  .join('')}
bestätigt.`,
            visibleForRoles: [
              UserRoleEnum.ADMIN,
              UserRoleEnum.BRAND,
              UserRoleEnum.PARTNER,
              UserRoleEnum.CUSTOMER,
            ],
            actionType: CommentActionTypeEnum.GENERIC,
          },
        })
        .then(({ data }) => {
          this.changeResult = {
            type: 'success',
            title: this.$t('successful'),
          };
          this.change = null;
          this.loadData();
          this.scrollActionsIntoView = true;
          this.$store.dispatch(COUNTS_REFRESH);
        });
    },
    sendChangeRequest() {
      if (this.sendInputs.appointment_date) {
        if (
          !this.sendInputs.appointment_date.start ||
          !this.sendInputs.appointment_date.end
        ) {
          this.changeResult = {
            type: 'danger',
            title: this.$t('_errors.general.title'),
            description: this.$t('inputs.appointment-date-incomplete'),
          };
          this.change.loading = false;
          return;
        }
      }

      axios
        .post(`/api/partner/orders/set-status`, {
          preview: false,
          job_id: this.job_id,
          ...this.requestMixin,
          inputs: this.sendInputs,
        })
        .then((response) => {
          this.order = response.data;
          // GID-2608: re-baseline so a resume/idle check deferred during this edit
          // diffs against the just-submitted state, not the stale pre-submit one
          // (otherwise it would redirect/refresh on the user's own change).
          this.captureChangeBaseline();
          this.changeResult = {
            type: 'success',
            title: this.$t('successful'),
          };
          this.gaTrack();
          this.afterChange(this.order.job.status);
          this.new_custom_services = [];
          this.signView = false;
          this.change = null;
          this.$store.dispatch(COUNTS_REFRESH);
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status == 422) {
              const allErrors = this.translateErrors(
                error.response.data.errors,
              );
              const nonInputErrors = filterNonInputErrors(
                allErrors,
                Object.keys(this.sendInputs),
              );
              if (Object.keys(nonInputErrors).length > 0) {
                this.changeResult = {
                  type: 'danger',
                  title:
                    this.$t('_errors.server.title') +
                    ` - ${error.response.status}`,
                  description: this.$t('_errors.server.details'),
                  validationErrors: nonInputErrors,
                };
              } else if (
                Object.keys(allErrors).length > 0 &&
                allErrors.services
              ) {
                this.changeResult = {
                  type: 'error',
                  title:
                    this.$t('_errors.server.title') +
                    ` - ${error.response.status}`,
                  description: this.$t('_errors.server.details'),
                  details: { errors: allErrors },
                };
              }
              this.$store.dispatch(`validation/${STORE_API_ERRORS}`, {
                context: this.validationContext,
                errors: allErrors,
              });
            } else {
              const { details } = this.stipFilesFromInput(error.response);
              this.changeResult = {
                type: 'danger',
                title:
                  this.$t('_errors.server.title') +
                  ` - ${error.response.status}`,
                description: this.$t('_errors.server.details'),
                details,
              };
            }
          } else {
            this.changeResult = {
              type: 'danger',
              title: this.$t('_errors.network.title'),
              description: this.$t('_errors.network.details'),
            };
          }
          this.signView = false;
          this.change.loading = false;
        });
    },
    afterChange(status) {
      this.showCRFormDocumentation = false;
      switch (status) {
        case JobStatusEnum.OPEN:
          this.$router.push({ name: 'offer', params: { job_id: this.job_id } });
          break;
        case JobStatusEnum.FINISHED:
        case JobStatusEnum.APPROVAL_PENDING:
        case JobStatusEnum.COMPLETED:
          if (
            this.order.opportunity.can_create_proposal?.includes(
              UserRoleEnum.PARTNER,
            ) &&
            this.order.is_precheck &&
            this.order.is_remote
          ) {
            this.$router.push({
              name: 'create',
              params: { job_id: this.job_id },
            });
            break;
          }
        // eslint-disable-next-line no-fallthrough
        case JobStatusEnum.CLOSED:
        case JobStatusEnum.CANCELLED:
          this.$router.push({
            name: 'order-complete',
            params: { job_id: this.job_id },
          });
          break;
        default:
          this.fetchAvailableActions();
      }
    },
    cancelChange() {
      this.showCRFormDocumentation = false;
      if (this.signView) {
        this.signView = false;
      } else {
        this.change = null;
        this.changeResult = null;
        this.tryAutoStatusAction();
      }
    },

    showSignView() {
      const hasCustomServiceErrors = this.customServiceHasErrors();
      if (!hasCustomServiceErrors) {
        this.$refs.observer.validate().then((valid) => {
          if (valid) {
            this.signView = true;
          }
        });
      }
    },
    /** check if custom service has error, name or type not assigned to custom service **/
    customServiceHasErrors() {
      if (this.newCustomServicesHaveErrors) {
        this.new_custom_services.forEach((srvc, index) => {
          if (!srvc.name || srvc.name.trim() === '') {
            this.$store.dispatch('CUSTOM_SERVICE_PUSH_ERROR', {
              index,
              errorString: 'order-details.custom-service-error.service-name',
            }); // show form error for missing name
          } else if (!srvc.service_type) {
            this.$store.dispatch('CUSTOM_SERVICE_PUSH_ERROR', {
              index,
              errorString: 'order-details.custom-service-error.service-type',
            }); // show form error for missing service type
          } else {
            this.$store.dispatch('CUSTOM_SERVICE_PUSH_ERROR', { index });
          }
        });
        return true;
      } else {
        this.$store.dispatch('CUSTOM_SERVICES_RESET_ERRORS'); // clean form error
        return false;
      }
    },
    createValidationContext(context) {
      this.$store.dispatch(`validation/${CONTEXT_CREATE}`, {
        context: `${VALIDATION_CONTEXT_PREFIX}-${context}`,
      });
    },
    destroyValidationContext(context) {
      this.$store.dispatch(`validation/${CONTEXT_DESTROY}`, {
        context: `${VALIDATION_CONTEXT_PREFIX}-${context}`,
      });
    },
    gaTrack() {
      if (this.requestMixin.new_status) {
        switch (this.order.job.status) {
          case 'started':
            this.gaTrackStart();
            break;
          case 'appointment confirmed':
            this.gaTrackAppointmentSet();
            break;
          case 'finished':
            this.gaTrackFinish();
            break;
        }
      } else if (this.change.cancellation) {
        this.gaTrackCancellation(this.change.cancellation.reason_category);
      }
    },
    gaTrackStart() {
      const now = this.$moment();
      const nowHour = now.hour();
      const dayOfWeek = now.format('dddd');
      let timeOfDay;

      if (nowHour < 11) {
        timeOfDay = 'Morgen';
      } else if (nowHour >= 11 && nowHour < 13) {
        timeOfDay = 'Mittag';
      } else if (nowHour >= 13 && nowHour < 17) {
        timeOfDay = 'Nachmittag';
      } else {
        timeOfDay = 'Abend';
      }

      this.$gtag.event('Start', {
        event_category: 'Time tracking',
        event_label: `${dayOfWeek} ${timeOfDay}`,
      });
    },
    gaTrackFinish() {
      const dateStart = this.$moment(this.order.date_start);
      const dateCompare = this.$moment();
      const dateDiff = dateCompare.diff(dateStart, 'seconds');

      this.$gtag.event('Stop', {
        event_category: 'Time tracking',
        event_label: 'Seconds',
        event_value: dateDiff,
      });
    },
    gaTrackAppointmentSet() {
      const dateCreated = this.$moment(this.order.job.created_date);
      const dateCompare = this.$moment(
        this.order.job.appointment_start_timestamp,
      );
      const dateDiff = dateCompare.diff(dateCreated, 'days');

      this.$gtag.event('Enter initial date', {
        event_category: 'Appointment date',
        event_label: dateDiff,
      });
    },
    gaTrackCancellation(category) {
      this.$gtag.event(category, {
        event_category: 'Cancellations',
        event_label: this.order.opportunity.name,
      });
    },
    stipFilesFromInput(response) {
      const details = response;
      let usesSignature = false;
      details.config.data = JSON.parse(details.config.data);
      Object.keys(details.config.data.inputs).forEach((key) => {
        const input = details.config.data.inputs[key];
        if (input && input.files instanceof Array) {
          input.files = input.files.map(({ mimetype }) => {
            if (mimetype == 'image/svg+xml') {
              usesSignature = true;
            }
            return {
              data: '...',
              mimetype,
            };
          });
        }
      });
      return { details, usesSignature };
    },
    isStatusAllowed(toStatus) {
      return !(
        this.order.job.status in this.disallowedActions &&
        toStatus.name in this.disallowedActions[this.order.job.status] &&
        this.disallowedActions[this.order.job.status][
          toStatus.name
        ].conditions.some((x) => x === true)
      );
    },
    actionButtonVariantStyle(action) {
      if (
        action.status === 'appointment confirmed' &&
        Object.values(this.statusActions)
          .map((x) => x.status)
          .includes('started')
      ) {
        return 'outline-primary';
      }
      return 'primary';
    },
  },
};
</script>

<style lang="scss">
@import '@gid/vue-common/scss/_variables.scss';
@import '~bootstrap/scss/functions';
@import '~bootstrap/scss/variables';
@import '~bootstrap/scss/mixins/breakpoints';

// Completion-checklist action buttons follow the design-token roles:
// the two form actions (protocol + customer-signature "Jetzt auffordern") are
// the Task role — Action Orange (#FF7043) outline. Secondary actions
// (Begründen / Auftragsänderung) keep their gray outline-secondary look.
.started-completion__action {
  .customer-signature-notice {
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;
    gap: 0.75rem !important;

    // "Achtung! Noch nicht hinzugefügt." is redundant inside the checklist —
    // the OFFEN badge + step description already convey it.
    p {
      display: none;
    }
  }

  .btn {
    white-space: nowrap;
  }

  .started-completion__cta,
  .customer-signature-notice .btn-customer-signature {
    color: #ff7043;
    background-color: transparent;
    border-color: #ff7043;

    &:hover,
    &:focus,
    &:active {
      color: #fff;
      background-color: #ff7043;
      border-color: #ff7043;
    }
  }
}

.gid-cr-select {
  .dropdown-menu {
    max-width: calc(100vw - #{$grid-gutter-width});
    max-height: calc(100vh - #{$grid-gutter-width});
    overflow-y: auto;
    z-index: $zindex-tooltip;
  }
  .dropdown-item {
    white-space: normal;
  }
}

.gid-sign-view {
  position: fixed;
  z-index: $zindex-modal;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 0 $grid-gutter-width * 0.5;
  background: $light;
  overflow-x: hidden;
  overflow-y: auto;
}

body.gid-sign-view--open {
  overflow: hidden;
}

.btn.btn-confirm-action-warning {
  background-color: #ff7043;
  border-color: #ff7043;

  &:hover {
    background-color: darken(#ff7043, 10%);
    border-color: darken(#ff7043, 10%);
  }
}

.disabled-prompt {
  z-index: 1;
  top: 0;
  display: flex;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  justify-content: center;
  align-items: center;
  background-color: #00000033;

  .box {
    background-color: white;
    padding: 2.8rem;
    font-weight: bold;
    font-size: 1.6rem;
  }
}

.oi-disabled {
  opacity: 0.7;
  pointer-events: none;
}
</style>

<style scoped>
.brand-logo {
  max-height: 24px;
  max-width: 120px;
  width: auto;
  height: auto;
  object-fit: contain;
}
</style>
